import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { BehaviorSubject } from 'rxjs';
import { normalizeSearchText } from 'src/app/core/_utils/utils.service';

export class ItemFlatNode {
  displayName?: string;
  level?: number;
  resourceId?: string;
  expandable?: boolean;
  code?: string;
  ceus?: [];
  detailedResource?: boolean;
  workUnit?: string;
}

export class ItemNode {
  children!: ItemNode[];
  displayName!: string;
  resourceId?: string;
  code?: string;
  ceus?: [];
  detailedResource?: boolean;
  workUnit?: string;
}

@Component({
  selector: 'app-dialog-change-engin',
  templateUrl: './dialog-change-engin.component.html',
  styleUrls: ['./dialog-change-engin.component.scss']
})
export class DialogChangeEnginComponent {
  flatNodeMap = new Map<ItemFlatNode, ItemNode>();
  nestedNodeMap = new Map<ItemNode, ItemFlatNode>();
  selectedParent: ItemFlatNode | null = null;
  treeControl: FlatTreeControl<ItemFlatNode>;
  treeFlattener!: MatTreeFlattener<ItemNode, ItemFlatNode>;
  dataSource!: MatTreeFlatDataSource<ItemNode, ItemFlatNode>;
  checklistSelection = new SelectionModel<ItemFlatNode>(true);
  dataChange = new BehaviorSubject<ItemNode[]>([]);
  dataService: ItemNode[] = [];
  showOnlyFavorites: boolean = false;
  refItems!: any[];

  constructor(
    public dialogRef: MatDialogRef<DialogChangeEnginComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    if (data) {
      this.initialize(data.fullTree);
    }

    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<ItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    this.dataChange.subscribe((data) => {
      this.dataSource.data = data;
    });
  }

  initialize(data: any) {
    this.dataService = this.buildFileTree(data);
    this.refItems = data;
    // Notify the change.
    this.dataChange.next(this.dataService);
  }

  getLevel = (node: ItemFlatNode) => node.level || 0;

  getChildren = (node: ItemNode): any[] => node.children;

  hasNoContent = (_: number, _nodeData: ItemFlatNode) => _nodeData.displayName === '';

  //get dataIems(): ItemNode[] { return this.dataChange.value; }

  onClickBack() {
    this.dialogRef.close();
  }

  onClickAdd() {
    return this.checklistSelection?.selected.filter((x) => x.resourceId != null);
  }

  nameChanged(target: any) {
    if (this.dataService.length > 0 && target?.value?.length < 2) {
      this.dataChange.next(this.dataService);
    }

    let filteredData: any[] = [];

    if (target?.value?.length < 2) {
      this.dataChange.next(this.dataService);
      return;
    }

    const searchValue = normalizeSearchText(target?.value);
    let filteredParents: any[] = [];

    filteredParents = this.dataService.filter(
      (c) =>
        normalizeSearchText(c.displayName).includes(searchValue) ||
        normalizeSearchText(c.code).includes(searchValue) ||
        c.children.some(
          (x) =>
            normalizeSearchText(x.displayName).includes(searchValue) ||
            normalizeSearchText(x.code).includes(searchValue) ||
            x.children?.some(
              (y) =>
                normalizeSearchText(y.displayName).includes(searchValue) ||
                normalizeSearchText(y.code).includes(searchValue) ||
                y.children?.some((z) => normalizeSearchText(z.displayName).includes(searchValue) || normalizeSearchText(z.code).includes(searchValue))
            )
        )
    );

    filteredParents.forEach((fP) => {
      let filteredChildrenValue: any[] = [];

      let filteredChildren = fP.children.filter(
        (v: { displayName: string; code: string; children: { displayName: string; code: string }[] }) =>
          normalizeSearchText(v.displayName).includes(searchValue) ||
          normalizeSearchText(v.code).includes(searchValue) ||
          v.children?.some((y: { displayName: string; code: string }) => normalizeSearchText(y.displayName).includes(searchValue) || normalizeSearchText(y.code).includes(searchValue))
      );

      filteredChildren.forEach((fC: { children: any[]; displayName: any; resourceId: any; code: any; ceus: any; detailedResource: any; workUnit: any }) => {
        let filteredChildrenNext = fC.children?.filter((z: { displayName: string; code: string }) => normalizeSearchText(z.displayName).includes(searchValue) || normalizeSearchText(z.code).includes(searchValue));

        let item = {
          children: filteredChildrenNext,
          displayName: fC.displayName,
          resourceId: fC.resourceId,
          code: fC.code,
          ceus: fC.ceus,
          detailedResource: fC.detailedResource,
          workUnit: fC.workUnit
        };

        filteredChildrenValue.push(item);
      });

      let item = {
        children: filteredChildrenValue,
        displayName: fP.displayName,
        resourceId: fP.resourceId,
        code: fP.code,
        ceus: fP.ceus,
        detailedResource: fP.detailedResource,
        workUnit: fP.workUnit
      };

      filteredData.push(item);
    });

    this.dataChange.next(filteredData);
    this.treeControl.expandAll();
  }

  favoriteToggle() {
    this.showOnlyFavorites ? this.initialize(this.data.favoriteData) : this.initialize(this.data.fullTree);
  }

  isExpandable = (node: ItemFlatNode) => node.expandable || false;

  transformer = (node: ItemNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.displayName === node.displayName ? existingNode : new ItemFlatNode();
    flatNode.displayName = node.displayName;
    flatNode.level = level;
    flatNode.resourceId = node.resourceId != node.displayName ? node.resourceId : undefined;
    flatNode.code = node.code;
    flatNode.detailedResource = node.detailedResource;
    flatNode.ceus = node?.ceus;
    flatNode.workUnit = node?.workUnit;
    flatNode.expandable = !!node.children?.length;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  leafItemSelectionToggle(node: ItemFlatNode): void {
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
  }

  checkAllParentsSelection(node: ItemFlatNode): void {
    let parent: ItemFlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  checkRootNodeSelection(node: ItemFlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      });
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  getParentNode(node: ItemFlatNode): ItemFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel && currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (currentLevel && this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  descendantsAllSelected(node: ItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      });
    return descAllSelected;
  }

  descendantsPartiallySelected(node: ItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  itemSelectionToggle(node: ItemFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node) ? this.checklistSelection.select(...descendants) : this.checklistSelection.deselect(...descendants);

    descendants.forEach((child) => this.checklistSelection.isSelected(child));
    this.checkAllParentsSelection(node);
  }

  hasChild = (_: number, _nodeData: ItemFlatNode) => _nodeData.expandable;

  buildFileTree(obj: { [key: string]: any }, level: number = 0): ItemNode[] {
    return Object.keys(obj).reduce<ItemNode[]>((accumulator, key) => {
      const value = obj[key];
      const node = new ItemNode();

      if (value.value != null && level == 0) {
        node.displayName = value.key;
        if (typeof value === 'object') {
          const ownGroups = value.value.filter((v: { key: string | null }) => v.key == null || v.key === value.key);
          const industryGroups = value.value.filter((v: { key: string | null }) => v.key != null && v.key !== value.key);
          node.children = ownGroups.reduce((children: ItemNode[], group: { value: any }) => [...children, ...this.buildFileTree(group.value, level + 1)], []);
          if (industryGroups.length > 0) {
            node.children = [...node.children, ...this.buildFileTree(industryGroups, level + 1)];
          }
        }
      } else if (level == 1 && value.key != null) {
        node.displayName = value.key;
        node.children = this.buildFileTree(value.value, level + 1);
      } else if (value != null && level == 0) {
        node.displayName = key;
        if (typeof value === 'object') {
          node.children = this.buildFileTree(value, level + 1);
        }
      } else {
        node.resourceId = value?.resourceId;
        node.displayName = value?.displayName;
        node.code = value?.code;
        node.detailedResource = value?.detailedResource;
        if (value?.ceus) {
          node.ceus = value?.ceus;
        }
        if (value?.workUnit) {
          node.workUnit = value?.workUnit;
        }
      }

      return accumulator.concat(node);
    }, []);
  }
}
